import React, {useState} from 'react'
import {ButtonConfirm} from '@therms/atalaya'
import {Alerts, DateTime, useRPCContext} from '@therms/react-modules'
import {RPCDocs} from '@therms/rpc-gateway'
import {useCanCompleteLocationTaskTodo} from '@src/hooks/useCanCompleteLocationTaskTodo'
import dayjs from 'dayjs'
import {humanizeMinutes} from '@therms/web-js'
import classNames from 'classnames'

interface LocationTaskTodoListCardProps {
  locationTaskTodo: RPCDocs.Locations.GetLocationTaskTodos.v1.LocationTaskTodo
}

export function LocationTaskTodoListCard({
  locationTaskTodo: todo,
}: LocationTaskTodoListCardProps) {
  const {rpcService} = useRPCContext()
  const [completingTodo, setCompletingTodo] = useState(false)
  const [completed, setCompleted] = useState(false)

  const {canComplete, message, minAway, minPast, reason} =
    useCanCompleteLocationTaskTodo(todo)

  const handleComplete = (args: {
    status: 'skipped' | 'completed'
    locationTaskTodoId: string
  }) => {
    setCompletingTodo(true)
    rpcService.Locations.CompleteLocationTaskTodo.v1
      .call({
        locationTaskTodo: {
          id: args.locationTaskTodoId,
        },
        status:
          args.status as RPCDocs.Locations.CompleteLocationTaskTodo.v1.Status,
      })
      .catch((error: any) => {
        Alerts.error(
          'An error occurred while completing the task.',
          error.message,
        )
        console.error('Error completing location task todo:', error.message)
      })
      .then(() => {
        setCompleted(true)
        Alerts.success('Task ' + args.status)
      })
      .finally(() => setCompletingTodo(false))
  }

  return (
    <div
      className={classNames(
        {'opacity-50': completed},
        'p-base relative justify-between sm:flex',
      )}
    >
      <div>
        <div className="text-md font-semibold">{todo.title}</div>

        <div className="text-color-faded text-sm first-letter:capitalize">
          {todo.description}
        </div>

        <div className="text-color-strong mt-base flex text-center font-bold">
          {todo.allowedEarliest ? (
            <>
              {dayjs(todo.allowedEarliest).format('MMM DD,')}
              <DateTime mode="time" timestamp={todo.allowedEarliest} />
            </>
          ) : (
            <>
              {dayjs(todo.dueDate).format('MMM DD,')}
              <DateTime mode="time" timestamp={todo.dueDate} />
            </>
          )}
          {todo.allowedLatest && (
            <>
              <span>- </span>
              <DateTime mode="time" timestamp={todo.allowedLatest} />
            </>
          )}
        </div>
      </div>

      <div className="gap-xxs mt-sm flex min-w-40 sm:mt-0 sm:flex-col">
        {!completed &&
          todo.status ===
            RPCDocs.Locations.GetLocationTaskTodos.v1.Status.Pending && (
          <ButtonConfirm
            className="min-w-16"
            disabled={completingTodo}
            onClick={() => {
              handleComplete({
                locationTaskTodoId: todo.id,
                status: 'skipped',
              })
            }}
            variant="caution"
            outline
          >
            Skip
          </ButtonConfirm>
        )}

        {canComplete && (
          <ButtonConfirm
            className="grow"
            disabled={completingTodo}
            onClick={() => {
              handleComplete({
                locationTaskTodoId: todo.id,
                status: 'completed',
              })
            }}
            variant="positive"
          >
            Complete
          </ButtonConfirm>
        )}

        {!canComplete && (
          <>
            {reason === 'too_early' && !!minAway && (
              <div className="text-color-neutral border-border p-xs flex grow items-center justify-center rounded border-2 border-dashed text-center text-sm font-medium italic">
                Can complete in {humanizeMinutes(minAway)}.
              </div>
            )}

            {reason === 'too_late' && minPast !== undefined && (
              <div className="border-caution-faded p-xs text-caution flex grow items-center justify-center rounded border-2 border-dashed text-center text-sm font-bold">
                <span className="text-caution text-sm font-bold">Missed</span>
              </div>
            )}

            {reason === 'already_completed' && minPast !== undefined && (
              <div className="border-positive-faded p-xs text-color-positive flex grow items-center justify-center rounded border-2 border-dashed text-center text-sm font-bold">
                Completed
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
